import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Sparkles, 
  Plus,
  FileText,
  Download,
  ArrowRight
} from 'lucide-react';

export const HomePage: React.FC = () => {
  return (
    <div className="main-content">
      {/* En-tête simple */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <div className="flex items-center justify-center mb-6">
          <Sparkles className="h-12 w-12 text-magical-purple mr-4" />
          <h1 className="text-4xl font-elegant font-bold text-gradient">
            Générateur de Livres Illustrés
          </h1>
        </div>
        <p className="text-lg text-gray-600 font-children">
          Créez des livres illustrés pour enfants avec l'IA
        </p>
      </motion.div>

      {/* Actions rapides */}
      <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Link 
            to="/project/new"
            className="card-magical hover:scale-105 transition-all duration-200 block"
          >
            <div className="text-center">
              <Plus className="h-16 w-16 text-magical-purple mx-auto mb-4" />
              <h3 className="text-xl font-elegant font-semibold mb-2">
                Nouveau Livre
              </h3>
              <p className="text-gray-600 font-children">
                Commencez un nouveau projet de livre illustré
              </p>
            </div>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Link 
            to="/dashboard"
            className="card hover:scale-105 transition-all duration-200 block"
          >
            <div className="text-center">
              <BookOpen className="h-16 w-16 text-secondary-500 mx-auto mb-4" />
              <h3 className="text-xl font-elegant font-semibold mb-2">
                Mes Projets
              </h3>
              <p className="text-gray-600 font-children">
                Gérez et continuez vos livres en cours
              </p>
            </div>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="card bg-gradient-to-br from-gray-50 to-gray-100"
        >
          <div className="text-center">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-elegant font-semibold mb-2 text-gray-500">
              Modèles
            </h3>
            <p className="text-gray-500 font-children">
              Bientôt : modèles prêts à utiliser
            </p>
          </div>
        </motion.div>
      </div>

      {/* Workflow simple */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-16 max-w-3xl mx-auto"
      >
        <h2 className="text-2xl font-elegant font-semibold text-center mb-8">
          Comment ça fonctionne
        </h2>
        
        <div className="grid md:grid-cols-4 gap-6 text-center">
          <div className="relative">
            <div className="h-12 w-12 bg-gradient-to-r from-magical-purple to-magical-pink rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-3">
              1
            </div>
            <h4 className="font-children font-semibold mb-2">Texte</h4>
            <p className="text-sm text-gray-600">Écrivez ou collez votre histoire</p>
            <ArrowRight className="h-4 w-4 text-gray-400 mx-auto mt-3 hidden md:block" />
          </div>
          
          <div className="relative">
            <div className="h-12 w-12 bg-gradient-to-r from-magical-purple to-magical-pink rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-3">
              2
            </div>
            <h4 className="font-children font-semibold mb-2">Personnages</h4>
            <p className="text-sm text-gray-600">Décrivez vos personnages</p>
            <ArrowRight className="h-4 w-4 text-gray-400 mx-auto mt-3 hidden md:block" />
          </div>
          
          <div className="relative">
            <div className="h-12 w-12 bg-gradient-to-r from-magical-purple to-magical-pink rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-3">
              3
            </div>
            <h4 className="font-children font-semibold mb-2">Génération</h4>
            <p className="text-sm text-gray-600">L'IA crée les illustrations</p>
            <ArrowRight className="h-4 w-4 text-gray-400 mx-auto mt-3 hidden md:block" />
          </div>
          
          <div>
            <div className="h-12 w-12 bg-gradient-to-r from-magical-purple to-magical-pink rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-3">
              4
            </div>
            <h4 className="font-children font-semibold mb-2">Export</h4>
            <p className="text-sm text-gray-600">Téléchargez le PDF</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
